import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import useAuthStore from "./store/authStore";
import RoleProtectedRoute from "./components/RoleProtectedRoute";

// Lazy load pages for code splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const SetPasswordPage = lazy(() => import("./pages/SetPasswordPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AgentDashboard = lazy(() => import("./pages/AgentDashboard"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const ChatbotPage = lazy(() => import("./pages/ChatbotPage"));
const GIQuotationPage = lazy(() => import("./pages/GIQuotationPage"));
const AccountManagementPage = lazy(() => import("./pages/AccountManagementPage"));
const TechPage = lazy(() => import("./pages/TechPage"));
const LLMEvaluationPage = lazy(() => import("./pages/LLMEvaluationPage"));
const TechN8NPage = lazy(() => import("./pages/TechN8NPage"));
const OCRPage = lazy(() => import("./pages/OCRPage"));
const PDFUnlockerPage = lazy(() => import("./pages/PDFUnlockerPage"));
const QRCodePage = lazy(() => import("./pages/QRCodePage"));
const CRMPage = lazy(() => import("./pages/CRMPage"));
const CRMAdminPage = lazy(() => import("./pages/CRMAdminPage"));
const CommissionPage = lazy(() => import("./pages/CommissionPage"));
const CommissionAdminPage = lazy(() => import("./pages/CommissionAdminPage"));
const CompliancePage = lazy(() => import("./pages/CompliancePage"));
const ComplianceAdminPage = lazy(() => import("./pages/ComplianceAdminPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const BMSSetupPage = lazy(() => import("./pages/BMSSetupPage"));
const CMSPage = lazy(() => import("./pages/CMSPage"));
const CMSAdminPage = lazy(() => import("./pages/CMSAdminPage"));
const CMSDetailPage = lazy(() => import("./pages/CMSDetailPage"));
const KnowledgeBasePage = lazy(() => import("./pages/KnowledgeBasePage"));

// Protected route wrapper that preserves location on redirect
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, status } = useAuthStore();
  const location = useLocation();

  // Wait for auth to finish initializing (status can be "idle" or "loading")
  if (status !== "ready") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading session…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Preserve the current location so user can be redirected back after login
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function App() {
  const { initialize, status } = useAuthStore();

  useEffect(() => {
    initialize().catch((error) => {
      console.error("Failed to restore session", error);
    });
  }, [initialize]);

  if (status === "loading") {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading session…</div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-lg">Loading…</div>
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          
          {/* Dashboard Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/admin"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminDashboard />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/agent"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin", "agent"]}>
                  <AgentDashboard />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/client"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin", "agent", "client"]}>
                  <ClientPortal />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          {/* CMS Routes */}
          <Route
            path="/cms"
            element={
              <ProtectedRoute>
                <CMSPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cms/admin"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <CMSAdminPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cms/:id"
            element={
              <ProtectedRoute>
                <CMSDetailPage />
              </ProtectedRoute>
            }
          />

          {/* CRM Routes */}
          <Route
            path="/crm"
            element={
              <ProtectedRoute>
                <CRMPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/crm/admin"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <CRMAdminPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          {/* Commission Routes */}
          <Route
            path="/commission"
            element={
              <ProtectedRoute>
                <CommissionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/commission/admin"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <CommissionAdminPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          {/* Compliance Routes */}
          <Route
            path="/compliance"
            element={
              <ProtectedRoute>
                <CompliancePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/compliance/admin"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <ComplianceAdminPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          {/* Account Management */}
          <Route
            path="/account-management"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AccountManagementPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          {/* Admin Tools Routes */}
          <Route
            path="/admin-tools"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <AdminPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-tools/ocr"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <OCRPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-tools/pdf-unlocker"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <PDFUnlockerPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin-tools/qr-code"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <QRCodePage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          {/* Other Routes */}
          <Route
            path="/agent_chatbot"
            element={
              <ProtectedRoute>
                <ChatbotPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/agent/gi-quotation"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin", "agent"]}>
                  <GIQuotationPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tech"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <TechPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tech/llm-evaluation"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <LLMEvaluationPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tech/n8n"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <TechN8NPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/landing-page-setup"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <KnowledgeBasePage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bms-setup"
            element={
              <ProtectedRoute>
                <RoleProtectedRoute allowedRoles={["admin"]}>
                  <BMSSetupPage />
                </RoleProtectedRoute>
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;